import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { RichTextEditor } from "@/components/rich-text-editor";
import { formatTimeAgo, useAutosave } from "@/lib/hooks/use-autosave";
import {
  type CreatePostInput,
  useAdminPost,
  useCreatePost,
  useUpdatePost,
} from "@/lib/queries/use-posts";
import { cn } from "@/lib/utils";

// Sharp-edged document icon for Essay
function EssayIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="miter"
      strokeLinecap="square"
    >
      <path d="M6 2h8l6 6v14H6V2z" />
      <path d="M14 2v6h6" />
      <path d="M9 13h6" />
      <path d="M9 17h4" />
    </svg>
  );
}

// Sharp-edged pen icon for Note
function NoteIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="miter"
      strokeLinecap="square"
    >
      <path d="M4 20h4l12-12-4-4L4 16v4z" />
      <path d="M14 6l4 4" />
    </svg>
  );
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .trim();
}

export function AdminPostEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = id === "new";
  const postId = isNew ? 0 : parseInt(id || "0", 10);

  const { data: existingPost, isLoading } = useAdminPost(postId);
  const createPost = useCreatePost();
  const updatePost = useUpdatePost();

  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [excerpt, setExcerpt] = useState("");
  const [content, setContent] = useState("");
  const [type, setType] = useState<"essay" | "note">("essay");
  const [published, setPublished] = useState(false);
  const [autoSlug, setAutoSlug] = useState(true);

  // Load existing post data
  useEffect(() => {
    if (existingPost) {
      setTitle(existingPost.title);
      setSlug(existingPost.slug);
      setExcerpt(existingPost.excerpt || "");
      setContent(existingPost.content);
      setType(existingPost.type as "essay" | "note");
      setPublished(existingPost.published);
      setAutoSlug(false);
    }
  }, [existingPost]);

  // Auto-generate slug from title
  useEffect(() => {
    if (autoSlug && isNew) {
      setSlug(slugify(title));
    }
  }, [title, autoSlug, isNew]);

  // Memoize form data for autosave comparison
  const formData = useMemo(
    () => ({
      title,
      slug,
      excerpt,
      content,
      type,
      published,
    }),
    [title, slug, excerpt, content, type, published]
  );

  // Autosave callback
  const handleAutosave = useCallback(async () => {
    if (!title || !slug || isNew) return; // Don't autosave if required fields are empty or new post

    const postData: CreatePostInput = {
      title,
      slug,
      excerpt: excerpt || undefined,
      content,
      type,
      published,
      published_at: published ? new Date().toISOString() : undefined,
    };

    await updatePost.mutateAsync({ id: postId, ...postData });
  }, [title, slug, excerpt, content, type, published, postId, updatePost, isNew]);

  // Autosave hook - only enabled for existing posts
  const {
    lastSaved,
    isSaving: isAutosaving,
    hasUnsavedChanges,
    markAsSaved,
  } = useAutosave({
    data: formData,
    onSave: handleAutosave,
    enabled: !isNew && !isLoading,
  });

  // Update autosave state when existing post loads
  useEffect(() => {
    if (existingPost && !isNew) {
      markAsSaved();
    }
    // markAsSaved is stable (no deps), safe to exclude
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingPost, isNew]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const postData: CreatePostInput = {
      title,
      slug,
      excerpt: excerpt || undefined,
      content,
      type,
      published,
      published_at: published ? new Date().toISOString() : undefined,
    };

    try {
      if (isNew) {
        await createPost.mutateAsync(postData);
      } else {
        await updatePost.mutateAsync({ id: postId, ...postData });
        markAsSaved();
      }
      navigate("/admin/posts");
    } catch {
      // Error handling is done by React Query
    }
  };

  const isSaving = createPost.isPending || updatePost.isPending;

  // Status indicator component
  const SaveStatus = () => {
    if (isNew) return null;

    if (isAutosaving || isSaving) {
      return (
        <div className="flex items-center gap-2">
          <div className="size-2 bg-yellow-500 animate-pulse" />
          <span className="text-[11px] text-text-muted">Saving...</span>
        </div>
      );
    }

    if (hasUnsavedChanges) {
      return (
        <div className="flex items-center gap-2">
          <div className="size-2 bg-orange-500" />
          <span className="text-[11px] text-text-muted">Unsaved changes</span>
        </div>
      );
    }

    if (lastSaved) {
      return (
        <div className="flex items-center gap-2">
          <div className="size-2 bg-green-500" />
          <span className="text-[11px] text-text-muted">Saved {formatTimeAgo(lastSaved)}</span>
        </div>
      );
    }

    return null;
  };

  if (!isNew && isLoading) {
    return (
      <div>
        <p className="text-text-muted">Loading post...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 md:mb-8">
        <Link
          to="/admin/posts"
          className="inline-block text-[11px] md:text-[12px] text-text-muted hover:text-primary mb-2"
        >
          ← Back to posts
        </Link>
        <div className="flex items-center justify-between">
          <h1 className="text-[24px] md:text-[28px] font-bold text-primary">
            {isNew ? "New Post" : "Edit Post"}
          </h1>
          <SaveStatus />
        </div>
      </div>

      <form onSubmit={handleSubmit} className="max-w-3xl">
        {/* Title */}
        <div className="mb-6">
          <label className="block text-[11px] text-text-muted font-bold tracking-caps uppercase mb-2">
            Title
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Post title"
            className="w-full px-4 py-3 bg-card border border-border text-text-primary placeholder:text-text-faint focus:outline-none focus:border-border-hover"
            required
          />
        </div>

        {/* Slug */}
        <div className="mb-6">
          <label className="block text-[11px] text-text-muted font-bold tracking-caps uppercase mb-2">
            Slug
          </label>
          <input
            type="text"
            value={slug}
            onChange={(e) => {
              setSlug(e.target.value);
              setAutoSlug(false);
            }}
            placeholder="post-slug"
            className="w-full px-4 py-3 bg-card border border-border text-text-primary placeholder:text-text-faint focus:outline-none focus:border-border-hover"
            required
          />
          <p className="text-[11px] text-text-faint mt-1">URL: /writing/{slug || "post-slug"}</p>
        </div>

        {/* Type */}
        <div className="mb-6">
          <label className="block text-[11px] text-text-muted font-bold tracking-caps uppercase mb-2">
            Type
          </label>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setType("essay")}
              className={cn(
                "flex items-center gap-2 px-4 py-2.5 border transition-colors",
                type === "essay"
                  ? "border-primary bg-secondary text-primary"
                  : "border-border bg-transparent text-text-tertiary hover:border-border-hover hover:text-text-secondary"
              )}
            >
              <EssayIcon className="size-4" />
              <span className="text-[13px] font-medium">Essay</span>
            </button>
            <button
              type="button"
              onClick={() => setType("note")}
              className={cn(
                "flex items-center gap-2 px-4 py-2.5 border transition-colors",
                type === "note"
                  ? "border-primary bg-secondary text-primary"
                  : "border-border bg-transparent text-text-tertiary hover:border-border-hover hover:text-text-secondary"
              )}
            >
              <NoteIcon className="size-4" />
              <span className="text-[13px] font-medium">Note</span>
            </button>
          </div>
        </div>

        {/* Excerpt */}
        <div className="mb-6">
          <label className="block text-[11px] text-text-muted font-bold tracking-caps uppercase mb-2">
            Excerpt (optional)
          </label>
          <textarea
            value={excerpt}
            onChange={(e) => setExcerpt(e.target.value)}
            placeholder="A short summary of the post..."
            rows={2}
            className="w-full px-4 py-3 bg-card border border-border text-text-primary placeholder:text-text-faint focus:outline-none focus:border-border-hover resize-none"
          />
        </div>

        {/* Content - WYSIWYG Editor */}
        <div className="mb-6">
          <label className="block text-[11px] text-text-muted font-bold tracking-caps uppercase mb-2">
            Content
          </label>
          <RichTextEditor
            content={content}
            onChange={setContent}
            placeholder="Start writing your post..."
          />
        </div>

        {/* Publish Toggle */}
        <div className="mb-8">
          <button
            type="button"
            onClick={() => setPublished(!published)}
            className="flex items-center gap-3 group"
          >
            {/* Custom Square Toggle Switch - exact pixel values for equal spacing */}
            <div
              className={cn(
                "w-[36px] h-[18px] flex items-center py-[2px] pr-[2px] pl-[1px] transition-colors",
                published
                  ? "bg-primary justify-end"
                  : "bg-secondary border border-border justify-start"
              )}
            >
              <div
                className={cn("w-[14px] h-[14px]", published ? "bg-background" : "bg-text-muted")}
              />
            </div>
            <span
              className={cn(
                "text-[14px] transition-colors",
                published ? "text-primary" : "text-text-secondary"
              )}
            >
              Publish this post
            </span>
          </button>
          <p className="text-[11px] text-text-faint mt-1 ml-12">
            {published
              ? "This post will be visible on your site"
              : "This post will be saved as a draft"}
          </p>
        </div>

        {/* Actions */}
        <div className="flex flex-col md:flex-row gap-3 md:gap-4">
          <button
            type="submit"
            disabled={isSaving}
            className="w-full md:w-auto px-6 py-3 bg-primary text-primary-foreground font-bold text-[14px] hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {isSaving ? "Saving..." : isNew ? "Create Post" : "Save Changes"}
          </button>
          <Link
            to="/admin/posts"
            className="w-full md:w-auto px-6 py-3 border border-border text-text-secondary font-bold text-[14px] hover:border-border-hover transition-colors text-center"
          >
            Cancel
          </Link>
        </div>

        {(createPost.error || updatePost.error) && (
          <p className="text-destructive text-[13px] mt-4">
            Failed to save post. Please try again.
          </p>
        )}
      </form>
    </div>
  );
}
